import { CheckCircle2, CircleDashed, PencilLine } from "lucide-react";

import { ONBOARDING_STEPS, useOnboarding } from "@/hooks/doctor/use-onboarding";
import { validateStep, type StepId } from "@/services/onboarding/validation";
import { cn } from "@/lib/utils";
import type { OnboardingDraft } from "@/types/onboarding";

interface SummaryRow {
  label: string;
  value: string;
}

function summaryFor(step: StepId, draft: OnboardingDraft, currency: string): SummaryRow[] {
  const yes = (value: boolean) => (value ? "Yes" : "No");
  switch (step) {
    case "basics":
      return [
        { label: "Full name", value: draft.fullName },
        { label: "Gender", value: draft.gender },
        { label: "Mobile", value: draft.phone },
        { label: "Location", value: [draft.city, draft.region].filter(Boolean).join(", ") },
      ];
    case "specialization":
      return [
        { label: "Specialization", value: draft.specialization },
        { label: "Languages", value: draft.languages.join(", ") },
      ];
    case "education":
      return [
        { label: "Degree", value: draft.degree },
        { label: "Institute", value: draft.institute },
        { label: "Completed", value: draft.completionYear },
        { label: "Experience", value: draft.experience ? `${draft.experience} years` : "" },
      ];
    case "practice":
      return [
        {
          label: "Practice",
          value:
            draft.ownership === "own"
              ? "Owns an establishment"
              : draft.ownership === "visit"
                ? "Visits an establishment"
                : "",
        },
      ];
    case "establishment":
      return [
        { label: "Establishment", value: draft.establishmentName },
        {
          label: "Locality",
          value: [draft.locality, draft.establishmentCity].filter(Boolean).join(", "),
        },
      ];
    case "registration":
      return [
        { label: "Number", value: draft.registrationNumber },
        { label: "Council", value: draft.registrationCouncil },
        { label: "Year", value: draft.registrationYear },
      ];
    case "consultation":
      return [
        { label: "Fee", value: draft.fees ? `${currency}${draft.fees}` : "" },
        { label: "Video consultations", value: yes(draft.videoConsultationAvailable) },
        { label: "Bio", value: draft.bio },
      ];
    case "identity-proof":
      return [{ label: "Identity proof", value: draft.identityProof?.name ?? "" }];
    case "registration-proof":
      return [{ label: "Registration proof", value: draft.registrationProof?.name ?? "" }];
    case "establishment-proof":
      return [
        { label: "Role", value: draft.establishmentRole },
        { label: "Proof", value: draft.establishmentProof?.name ?? "" },
      ];
    case "location":
      return [
        { label: "Address", value: draft.mapAddress },
        {
          label: "Coordinates",
          value: [draft.latitude, draft.longitude].filter(Boolean).join(", "),
        },
      ];
    case "timings":
      return draft.timings
        .filter((day) => day.enabled)
        .map((day) => ({
          label: day.day,
          value: day.sessions.map((session) => `${session.from}–${session.to}`).join(", "),
        }));
    case "consultant":
      return draft.consultant
        ? [
            { label: "Consultant fee", value: `${currency}${draft.consultant.fees}` },
            {
              label: "Hours",
              value: draft.consultant.hoursSameAsEstablishment ? "Same as establishment" : "Custom",
            },
          ]
        : [{ label: "Consultant practice", value: "Not applicable" }];
    default:
      return [];
  }
}

export function ReviewStep() {
  const { draft, goTo, countryData } = useOnboarding();
  const currency = countryData?.meta.currencySymbol ?? "₹";
  const reviewable = ONBOARDING_STEPS.filter((step) => step.id !== "review");

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Check everything below, then submit for verification. You can jump back to any section.
      </p>

      {reviewable.map((step, index) => {
        const rows = summaryFor(step.id, draft, currency).filter((row) => row.value);
        const complete = Object.keys(validateStep(step.id, draft)).length === 0;
        return (
          <section key={step.id} className="rounded-xl border border-border bg-card p-4">
            <header className="flex items-center gap-2">
              {complete ? (
                <CheckCircle2 className="size-4 text-emerald-600" />
              ) : (
                <CircleDashed className="size-4 text-amber-600" />
              )}
              <h3 className="text-sm font-semibold">{step.title}</h3>
              <button
                type="button"
                onClick={() => goTo(index)}
                className="ml-auto inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                <PencilLine className="size-3" /> Edit
              </button>
            </header>
            {rows.length > 0 ? (
              <dl className="mt-3 grid gap-x-6 gap-y-2 sm:grid-cols-2">
                {rows.map((row) => (
                  <div key={row.label} className="min-w-0">
                    <dt className="text-xs text-muted-foreground">{row.label}</dt>
                    <dd className={cn("truncate text-sm font-medium")}>{row.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="mt-2 text-xs text-muted-foreground">Nothing added yet.</p>
            )}
          </section>
        );
      })}
    </div>
  );
}
